import { Controller, useFormContext } from "react-hook-form";
import Select, { components } from "react-select";
import CreatableSelect from "react-select/creatable";
import "./controls.scss";

interface Option {
  label: string;
  value: number | null;
  valueString?: string;
}

interface Props {
  name: string;
  placeholder: string;
  options: Option[];
  defaultValue?: Option | null | Option[];
  value?: Option | null | Option[];
  onChange?: (selectedOption: any) => void;
  required?: boolean;
  stopPropagation?: boolean;
  useValueString?: boolean;
  isMulti?: boolean;
  isCreatable?: boolean;
  rules?: any;
  isDisabled?: boolean;
}

const ValueContainer = ({ children, ...props }: any) => {
  const currentValues = props.getValue();
  if (props.isMulti && currentValues.length > 0) {
    // The children array contains [Value(s), Input]
    // We replace the values array with a single text element
    const [, input] = children;

    // Summary: Show first item and count of others to prevent UI overflow
    const label = currentValues.length > 1
      ? `${currentValues[0].label} (+${currentValues.length - 1} more)`
      : currentValues[0].label;

    return (
      <components.ValueContainer {...props}>
        <div
          className="select-text-value"
          style={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: 'calc(100% - 20px)'
          }}
        >
          {label}
        </div>
        {input}
      </components.ValueContainer>
    );
  }

  return <components.ValueContainer {...props}>{children}</components.ValueContainer>;
};

// We return null for MultiValue to hide the individual tag/rectangle boxes
const MultiValue = () => null;

export default function ESSearchDropdown(props: Props) {
  const {
    name,
    options,
    placeholder,
    defaultValue,
    value,
    required = true,
    onChange,
    stopPropagation = false,
    useValueString = false,
    isMulti = false,
    isCreatable = false,
    rules = {},
    ...rest
  } = props;

  const {
    control,
    formState: { errors },
  } = useFormContext();

  const errorMessage = errors && errors[name] && errors[name]?.message;

  const SelectComponent = isCreatable ? CreatableSelect : Select;

  return (
    <>
      <Controller
        control={control}
        name={name}
        rules={{
          ...(required ? { required: `${placeholder} is required` } : {}),
          ...rules
        }}
        render={({ field }) => {
          return (
            <div
              className="selectdropdown"
              onClick={(event: React.MouseEvent) => {
                if (stopPropagation) {
                  event.stopPropagation();
                }
              }}
              onKeyDown={(event: React.KeyboardEvent) => {
                if (stopPropagation && (event.key === "Enter" || event.key === " ")) {
                  event.stopPropagation();
                }
              }}
              tabIndex={0}
              role="presentation"
            >
              <SelectComponent
                {...rest}
                placeholder={placeholder}
                isClearable={true}
                isMulti={isMulti}
                options={options}
                classNamePrefix="select"
                getOptionValue={(option: any) => useValueString ? option?.valueString || "" : String(option?.value)}
                getOptionLabel={(option: any) => option?.label}
                value={field.value || defaultValue || (isMulti ? [] : null)}
                components={isMulti ? { ValueContainer, MultiValue } : {}}
                closeMenuOnSelect={!isMulti}
                hideSelectedOptions={false}
                onChange={(val: any) => {
                  field.onChange(val);
                  onChange && onChange(val);
                }}
                onBlur={field.onBlur}
                className="select-dropdown"
              />
            </div>
          );
        }}
      />
      {typeof errorMessage === "string" && (
        <span className="text-danger">{errorMessage}</span>
      )}
    </>
  );
}
